import { Validators } from "@angular/forms";
import { debounceTime, distinctUntilChanged, filter, startWith } from 'rxjs';
import { AxiosError } from "axios";
import { endOfDay, format, parseISO, subDays } from "date-fns";
import { CompanySearchAutocompleteItem } from "src/app/shared/company-search-service/company-search.service";
import { errorResponse } from "src/types/error-response";
import { InvoiceOcr } from "../../invoice-ocr/invoice-ocr.service";
import { AddressControls, ClientControls, ClientForm } from "../form.controls";
import { CLIENT_FORM_NAME, FormFactory } from "../form.factory";
import { Client } from "../form.resources";
import { maxDateValidator } from "../validators/max-date";

const IND_PARTY_TYPE_IDS: string[] = ['2-a', '999-a'];

export function getClientForm(this: FormFactory): ClientForm {
  const data = this.data as Client | undefined;

  const form = this.fb.group<ClientControls>({
    id: this.fb.control(data?.id ?? (this.formService.isGuest ? new Date().valueOf().toString() : null)),
    client_type: this.fb.control(data?.client_type ?? 'org', { nonNullable: true }),
    name: this.fb.control(data?.name ?? '', [Validators.required, Validators.maxLength(255)]),
    tax_number: this.fb.control(data?.tax_number ?? '', Validators.required),
    party_type_id: this.fb.control(data?.party_type_id ?? null),
    mother_name: this.fb.control(data?.mother_name ?? '', [Validators.required, Validators.maxLength(255)]),
    birth_place: this.fb.control(data?.birth_place ?? '', [Validators.required, Validators.maxLength(255)]),
    birth_date: this.fb.control(
      data?.birth_date
        ? (data.birth_date instanceof Date
          ? data.birth_date
          : parseISO(data.birth_date))
        : null,
      [Validators.required, maxDateValidator(endOfDay(subDays(new Date(), 1)))]
    ),
    address: this.fb.group<AddressControls>({
      country_iso: this.fb.control(data?.address.country_iso || 'HU', [Validators.required]),
      postcode: this.fb.control(data?.address.postcode ?? '', [Validators.required, Validators.maxLength(255)]),
      settlement: this.fb.control(data?.address.settlement ?? '', [Validators.required, Validators.maxLength(255)]),
      street: this.fb.control(data?.address.street ?? '', [Validators.required, Validators.maxLength(255)]),
    }),
  });

  form.controls.party_type_id.valueChanges
    .pipe(startWith(data?.party_type_id ?? null))
    .subscribe((value) => {
      if (
        form.controls.party_type_id.enabled
        && value
        && IND_PARTY_TYPE_IDS.includes(value)
      ) {
        form.controls.mother_name.enable();
        form.controls.birth_place.enable();
        form.controls.birth_date.enable();
      } else {
        form.controls.mother_name.disable();
        form.controls.birth_place.disable();
        form.controls.birth_date.disable();
      }
    });

  form.controls.client_type.valueChanges
    .pipe(
      startWith(data?.client_type),
      distinctUntilChanged(),
    )
    .subscribe(client_type => {
      if (client_type === 'house') {
        form.controls.tax_number.clearValidators();
        form.controls.tax_number.updateValueAndValidity();
        form.controls.party_type_id.disable();
      } else {
        form.controls.tax_number.setValidators(Validators.required);
        form.controls.tax_number.updateValueAndValidity();
        form.controls.party_type_id.enable();
      }
    });

  if (form.valid && !this.formService.isGuest) {
    form.disable();
    return form;
  }

  form.statusChanges
    .pipe(
      debounceTime(1000),
      filter(() => form.valid),
    )
    .subscribe({
      next: async () => {
        try {
          const value = form.getRawValue();
          const birthDateStr = value.birth_date instanceof Date
            ? format(value.birth_date, 'yyyy-MM-dd')
            : value.birth_date;

          await this.formService.updateClient({
            ...value,
            birth_date: birthDateStr,
          });
          this.trackValidForm(CLIENT_FORM_NAME);
        } catch (error) {
          console.error('Error while saving client', error);
          if (error instanceof AxiosError) {
            if (error.response?.status === 422) {
              const errorData = errorResponse.safeParse(error.response?.data);
              if (errorData.success) {
                Object.entries(errorData.data.errors).forEach(([path, errors]) => {
                  form.get(path)?.setErrors({
                    unknown: errors[0],
                  });
                });
              }
            }
          }
        }
      }
    });

  return form;
}

export function isClientMatchingInvoiceOcr(
  client: Client,
  invoice: InvoiceOcr
): boolean {
  return (
    (!client.name && !client.tax_number)
    || client.name === invoice.client_name
    || client.tax_number === invoice.client_tax_number
  );
}

export function mapInvoiceOcrToClient(
  invoice: InvoiceOcr,
  companyData: CompanySearchAutocompleteItem | null,
): Client {
  return {
    id: (new Date()).valueOf().toString(),
    name: companyData?.name ?? invoice.client_name ?? null,
    tax_number: companyData?.tax_number ?? invoice.client_tax_number ?? null,
    client_type: companyData ? 'org' : (invoice.client_type ?? 'org'),
    party_type_id: companyData?.party_type?.internal_id,
    birth_date: null,
    address: {
      country_iso: invoice.client_country_iso ?? null,
      postcode: companyData?.postcode ?? invoice.client_postcode ?? null,
      settlement: companyData?.settlement ?? invoice.client_city ?? null,
      street: companyData?.street ?? invoice.client_street ?? null,
    },
  };
};
